/**
 * 기준선 슬라이더 표.
 *
 * ★★ **원본 `settings_page` 는 4,178줄이다** — FF 가 슬라이더 하나마다 위젯
 * 트리를 복제했기 때문이고, 내용은 이 표가 전부다(`dsl/edit.dart`
 * `_thresholdGroups`).
 *
 * ⚠️ `reset` 은 App State 선언의 기본값과 **반드시 같아야 한다**.
 *
 * ★★ Flutter 에는 함정이 하나 있었다 — **`max` 를 좁히면 설정 화면이 죽었다.**
 * 브라우저에 남은 옛 값이 범위를 벗어나면 `Slider` 가 assert 로 터지고,
 * 「기본값 복원」 버튼이 **바로 그 죽은 화면에** 있어 빠져나갈 수 없었다.
 * ⚠️ `<input type="range">` 는 범위 밖 값을 조용히 가둬서 **그 함정이
 * 구조적으로 사라진다.** 그래도 범위를 좁히는 것은 여전히 신중해야 한다.
 */

export interface Threshold {
  label: string;
  field: ThresholdKey;
  min: number;
  max: number;
  step: number;
  reset: number;
  /** 원화 표기인가. ★ 표시만 바뀐다. */
  krw: boolean;
}

export type ThresholdKey =
  | 'targetCompletionRate'
  | 'minCompletionRate'
  | 'maxBlockedRate'
  | 'blockedBadRate'
  | 'maxNarrationFailRate'
  | 'narrationBadRate'
  | 'costWarnKrw'
  | 'costCapKrw'
  | 'usdKrwRate'
  | 'costFeeRate'
  | 'latencyWarnSec'
  | 'latencyBadSec';

export interface ThresholdGroup {
  title: string;
  note: string;
  items: Threshold[];
}

/** ★ `divisions` 는 `(max - min) / step` 이었다 — step 으로 환산해 적는다. */
export const THRESHOLD_GROUPS: ThresholdGroup[] = [
  {
    title: '완주율',
    note: '높을수록 좋다. 경고 기준 아래면 노랑, 위반 기준 아래면 빨강.',
    items: [
      { label: '경고 기준', field: 'targetCompletionRate', min: 0, max: 100, step: 1, reset: 50, krw: false },
      { label: '위반 기준', field: 'minCompletionRate', min: 0, max: 100, step: 1, reset: 35, krw: false },
    ],
  },
  {
    title: '진행 불가',
    note:
      '낮을수록 좋다. 판정에 도달하지 못한 턴의 비율입니다 — 안전 차단 · 대상 차단 · ' +
      '규칙상 불가능 · 의도 불명확 · 해석 실패 다섯 갈래의 합.',
    items: [
      { label: '경고 기준', field: 'maxBlockedRate', min: 0, max: 10, step: 0.1, reset: 1, krw: false },
      { label: '위반 기준', field: 'blockedBadRate', min: 0, max: 10, step: 0.1, reset: 2, krw: false },
    ],
  },
  {
    title: 'AI 오서술',
    note: '낮을수록 좋다. 서술 검증 실패 비율.',
    items: [
      { label: '경고 기준', field: 'maxNarrationFailRate', min: 0, max: 10, step: 0.1, reset: 2, krw: false },
      { label: '위반 기준', field: 'narrationBadRate', min: 0, max: 10, step: 0.1, reset: 4, krw: false },
    ],
  },
  {
    title: '회차당 원가',
    note:
      '표시 원가 = 실측 cost_usd 합 × (1 + 수수료율) × 환율 ÷ 회차 수. ' +
      '수수료율은 크레딧 충전 시 따로 내는 것이라 cost_usd 에 들어 있지 않습니다.',
    items: [
      { label: '경고 기준', field: 'costWarnKrw', min: 0, max: 2000, step: 50, reset: 750, krw: true },
      { label: '상한', field: 'costCapKrw', min: 0, max: 2000, step: 50, reset: 900, krw: true },
      { label: '환율 (원/USD)', field: 'usdKrwRate', min: 1000, max: 2000, step: 10, reset: 1450, krw: false },
      { label: '충전 수수료 (%)', field: 'costFeeRate', min: 0, max: 20, step: 0.5, reset: 8, krw: false },
    ],
  },
  {
    title: '응답 시간',
    note:
      'OBXYD 제안을 그대로 받았습니다(9회차 실측 P50 약 3.5초 · 최대 8초, LLM 호출 ' +
      '타임아웃 25초). 판정은 P95 로 합니다 — P50 은 거의 항상 초록이라 ' +
      '그러면 이 패널이 존재할 이유가 없습니다.',
    items: [
      { label: '경고 기준 (초)', field: 'latencyWarnSec', min: 0, max: 30, step: 0.5, reset: 5, krw: false },
      { label: '위반 기준 (초)', field: 'latencyBadSec', min: 0, max: 30, step: 0.5, reset: 12, krw: false },
    ],
  },
];

/** 이벤트 스키마 문서 — ★ 공통 필드. `lensMapEventDocs` 가 읽는 이름 그대로다. */
export const SCHEMA_COMMON: Array<[string, string]> = [
  ['event_name', '이벤트 종류'],
  ['run_id', '회차 식별자'],
  ['campaign_id', '캠페인'],
  ['chapter_id', '챕터'],
  ['scene_id', '장면'],
  ['content_version', '콘텐츠 버전'],
  ['schema_version', '이벤트 스키마 버전'],
  ['model_version', 'AI 모델'],
  ['prompt_version', '프롬프트 버전'],
  ['app_version', '앱 버전'],
  ['occurred_at', '발생 시각'],
  ['player_id', '설치 단위 식별자'],
  ['turn_index', '회차 안의 턴 번호'],
];
